import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  Compass,
  Sparkle,
} from "@phosphor-icons/react";

const countries = [
  { name: "India", currency: "INR", symbol: "₹" },
  { name: "United Arab Emirates", currency: "AED", symbol: "AED" },
  { name: "United States", currency: "USD", symbol: "$" },
  { name: "United Kingdom", currency: "GBP", symbol: "£" },
  { name: "Saudi Arabia", currency: "SAR", symbol: "SAR" },
  { name: "Qatar", currency: "QAR", symbol: "QAR" },
  { name: "Kuwait", currency: "KWD", symbol: "KWD" },
  { name: "Bahrain", currency: "BHD", symbol: "BHD" },
  { name: "Oman", currency: "OMR", symbol: "OMR" },
  { name: "Canada", currency: "CAD", symbol: "C$" },
  { name: "Australia", currency: "AUD", symbol: "A$" },
  { name: "Singapore", currency: "SGD", symbol: "S$" },
  { name: "Germany", currency: "EUR", symbol: "€" },
  { name: "France", currency: "EUR", symbol: "€" },
  { name: "Netherlands", currency: "EUR", symbol: "€" },
  { name: "Ireland", currency: "EUR", symbol: "€" },
  { name: "New Zealand", currency: "NZD", symbol: "NZ$" },
  { name: "South Africa", currency: "ZAR", symbol: "R" },
  { name: "Malaysia", currency: "MYR", symbol: "RM" },
  { name: "Japan", currency: "JPY", symbol: "¥" },
  { name: "Other", currency: "USD", symbol: "$" },
];

const educationOptions = [
  "High School",
  "Diploma",
  "Bachelor's",
  "Master's",
  "PhD",
  "Self-taught",
];

const getCountryInfo = (countryName) =>
  countries.find((c) => c.name === countryName) || null;

const steps = [
  {
    key: "name",
    label: "What's your name?",
    type: "text",
    placeholder: "Enter your full name",
  },
  {
    key: "age",
    label: "How old are you?",
    type: "number",
    placeholder: "Enter your age",
    min: "13",
    max: "100",
  },
  {
    key: "country",
    label: "Which country are you in?",
    type: "country",
  },
  {
    key: "education",
    label: "What's your highest education?",
    type: "education",
  },
  {
    key: "current_job",
    label: "What's your current role?",
    type: "text",
    placeholder: "Enter your current role",
  },
  {
    key: "current_salary",
    label: "What's your current annual salary?",
    type: "salary",
    placeholder: "Enter your current salary",
    min: "0",
    step: "1",
  },
  {
    key: "desired_career",
    label: "What's your dream career?",
    type: "text",
    placeholder: "Enter your target role",
  },
  {
    key: "desired_salary",
    label: "What's your target annual salary?",
    type: "salary",
    placeholder: "Enter your target salary",
    min: "0",
    step: "1",
  },
  {
    key: "study_hours_per_day",
    label: "How many hours/day can you study?",
    type: "number",
    placeholder: "Enter hours per day",
    min: "0.5",
    max: "24",
    step: "0.5",
  },
];

export default function Onboarding() {
  const { refresh } = useAuth();
  const nav = useNavigate();

  const [i, setI] = useState(0);

  // IMPORTANT: Completely blank for every new onboarding.
  const [data, setData] = useState({});

  const [submitting, setSubmitting] = useState(false);

  const cur = steps[i];
  const value = data[cur.key] ?? "";

  const countryInfo = getCountryInfo(data.country);

  const currency = countryInfo?.currency || "USD";
  const currencySymbol = countryInfo?.symbol || "$";

  const updateValue = (newValue) => {
    setData((prev) => ({
      ...prev,
      [cur.key]: newValue,
    }));
  };

  const validateCurrentStep = () => {
    if (
      value === "" ||
      value === null ||
      value === undefined
    ) {
      toast.error("Please fill this in");
      return false;
    }

    if (cur.key === "name") {
      const name = String(value).trim();

      if (name.length < 2) {
        toast.error("Please enter your full name");
        return false;
      }
    }

    if (cur.key === "age") {
      const age = Number(value);

      if (!Number.isInteger(age) || age < 13 || age > 100) {
        toast.error("Please enter a valid age between 13 and 100");
        return false;
      }
    }

    if (cur.key === "current_job" || cur.key === "desired_career") {
      const text = String(value).trim();

      if (text.length < 2) {
        toast.error("Please enter a valid role");
        return false;
      }

      const invalid = [
        "test",
        "testing",
        "abc",
        "xyz",
        "none",
        "n/a",
        "na",
        "nothing",
        "hello",
        "asdf",
      ];

      if (invalid.includes(text.toLowerCase())) {
        toast.error("Please enter your actual career or role");
        return false;
      }
    }

    if (
      cur.key === "current_salary" ||
      cur.key === "desired_salary"
    ) {
      const salary = Number(value);

      if (!Number.isFinite(salary) || salary < 0) {
        toast.error("Please enter a valid salary");
        return false;
      }
    }

    if (cur.key === "study_hours_per_day") {
      const hours = Number(value);

      if (!Number.isFinite(hours) || hours < 0.5 || hours > 24) {
        toast.error("Please enter between 0.5 and 24 hours");
        return false;
      }
    }

    return true;
  };

  const next = async () => {
    if (!validateCurrentStep()) return;

    if (i < steps.length - 1) {
      setI(i + 1);
      return;
    }

    setSubmitting(true);

    try {
      if (!countryInfo) {
        toast.error("Please select your country");
        setSubmitting(false);
        return;
      }

      const payload = {
        ...data,

        age: parseInt(data.age, 10),

        current_salary: parseFloat(data.current_salary),

        desired_salary: parseFloat(data.desired_salary),

        current_salary_currency: countryInfo.currency,

        desired_salary_currency: countryInfo.currency,

        currency: countryInfo.currency,

        currency_symbol: countryInfo.symbol,

        study_hours_per_day: parseFloat(
          data.study_hours_per_day
        ),
      };

      await api.post("/onboarding", payload);

      await refresh();

      toast.success("Roadmap generated!");

      nav("/dashboard");
    } catch (err) {
      console.error("Roadmap generation failed:", err);

      const detail = err.response?.data?.detail;
      const message =
        typeof detail === "string" && detail.trim()
          ? detail
          : err.request && !err.response
            ? "The AI service did not respond. Please check the connection and try again."
            : "Something went wrong while creating your roadmap. Please try again.";

      toast.error(message, {
        duration: 7000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const back = () => {
    if (i > 0) {
      setI(i - 1);
    }
  };

  const pct = ((i + 1) / steps.length) * 100;

  const salaryLabel =
    cur.key === "current_salary"
      ? countryInfo
        ? `What's your current annual salary in ${countryInfo.currency} (${countryInfo.symbol})?`
        : "What's your current annual salary?"
      : cur.key === "desired_salary"
        ? countryInfo
          ? `What's your target annual salary in ${countryInfo.currency} (${countryInfo.symbol})?`
          : "What's your target annual salary?"
        : cur.label;

  return (
    <div
      className="min-h-screen bg-[#050505] flex flex-col"
      data-testid="onboarding-page"
    >
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 sm:py-6 flex items-center justify-between max-w-2xl mx-auto w-full">
        <div className="inline-flex items-center gap-2">
          <Compass
            size={22}
            weight="duotone"
            className="text-blue-500"
          />

          <span className="font-medium tracking-tight">
            Career GPS{" "}
            <span className="text-blue-500">AI</span>
          </span>
        </div>

        <div className="text-xs text-zinc-500 font-mono">
          {i + 1} / {steps.length}
        </div>
      </div>

      {/* Progress */}
      <div className="px-4 sm:px-6 max-w-2xl mx-auto w-full">
        <div className="h-1 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-8">
        <div className="w-full max-w-2xl">

          <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-3 flex items-center gap-2">
            <Sparkle size={12} weight="fill" />
            Step {i + 1}
          </div>

          <h1
            className="text-2xl sm:text-3xl md:text-5xl tracking-tight font-medium"
            data-testid="onboarding-question"
          >
            {salaryLabel}
          </h1>

          <div className="mt-8 sm:mt-10">

            {/* COUNTRY */}
            {cur.type === "country" && (
              <select
                value={value}
                onChange={(e) => updateValue(e.target.value)}
                autoFocus
                data-testid="onb-input-country"
                className="w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 rounded-md px-4 py-4 text-white text-base sm:text-lg outline-none"
              >
                <option value="">
                  Select your country
                </option>

                {countries.map((country) => (
                  <option
                    key={country.name}
                    value={country.name}
                  >
                    {country.name} — {country.currency}
                  </option>
                ))}
              </select>
            )}

            {/* EDUCATION */}
            {cur.type === "education" && (
              <select
                value={value}
                onChange={(e) => updateValue(e.target.value)}
                autoFocus
                data-testid="onb-input-education"
                className="w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 rounded-md px-4 py-4 text-white text-base sm:text-lg outline-none"
              >
                <option value="">
                  Select your education
                </option>

                {educationOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}

            {/* SALARY */}
            {cur.type === "salary" && (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-xl sm:text-2xl text-zinc-500">
                    {currencySymbol}
                  </span>

                  <input
                    type="number"
                    min={cur.min}
                    step={cur.step}
                    placeholder={
                      countryInfo
                        ? `Enter amount in ${currency}`
                        : "Select country first"
                    }
                    value={value}
                    onChange={(e) =>
                      updateValue(e.target.value)
                    }
                    onKeyDown={(e) =>
                      e.key === "Enter" && next()
                    }
                    autoFocus
                    disabled={!countryInfo}
                    data-testid={`onb-input-${cur.key}`}
                    className="w-full bg-transparent border-b-2 border-white/10 focus:border-blue-500 px-0 py-3 sm:py-4 text-white text-xl sm:text-2xl md:text-3xl outline-none transition-colors disabled:opacity-40"
                  />
                </div>

                {countryInfo && (
                  <p className="mt-3 text-sm text-zinc-500">
                    Your salary will be stored in{" "}
                    <span className="text-zinc-300">
                      {currency}
                    </span>{" "}
                    and used with your country when creating your
                    career plan.
                  </p>
                )}
              </>
            )}

            {/* NORMAL INPUT */}
            {cur.type !== "country" &&
              cur.type !== "education" &&
              cur.type !== "salary" && (
                <input
                  type={cur.type}
                  min={cur.min}
                  max={cur.max}
                  step={cur.step}
                  placeholder={cur.placeholder}
                  value={value}
                  onChange={(e) =>
                    updateValue(e.target.value)
                  }
                  onKeyDown={(e) =>
                    e.key === "Enter" && next()
                  }
                  autoFocus
                  data-testid={`onb-input-${cur.key}`}
                  className="w-full bg-transparent border-b-2 border-white/10 focus:border-blue-500 px-0 py-3 sm:py-4 text-white text-xl sm:text-2xl md:text-3xl outline-none transition-colors"
                />
              )}

            {cur.key === "study_hours_per_day" && (
              <p className="mt-3 text-sm text-zinc-500">
                Example: 1, 1.5, 2, or 3 hours per day.
              </p>
            )}

          </div>

          {/* Navigation */}
          <div className="mt-8 sm:mt-10 flex items-center justify-between gap-4">

            <button
              onClick={back}
              disabled={i === 0}
              data-testid="onb-back"
              className="text-zinc-500 hover:text-white disabled:opacity-30 inline-flex items-center gap-2 transition-colors"
            >
              <ArrowLeft size={16} />
              Back
            </button>

            <button
              onClick={next}
              disabled={submitting}
              data-testid="onb-next"
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium px-6 sm:px-8 py-3 rounded-lg transition-colors inline-flex items-center gap-2 text-sm sm:text-base"
            >
              {submitting
                ? "Building roadmap..."
                : i === steps.length - 1
                  ? "Generate Roadmap"
                  : "Continue"}

              <ArrowRight size={16} />
            </button>

          </div>
        </div>
      </div>
    </div>
  );
}
